import type { ContentCardItem, ContentSearchItem, ClassificationResults, ContentMetadata } from '@content-search/domain';
import { toContentType } from '@content-search/domain';
import { getContentDisplayText } from '@core/utils';

/**
 * Helper function to process metadata filters
 */
export const processMetadataForCard = (metadata: Record<string, unknown>): Record<string, string> =>
  Object.fromEntries(Object.entries(metadata).map(([key, value]) => [key, String(value ?? '')]));

/**
 * Helper function to process custom values
 */
export const processCustomValuesForCard = (customValues: Record<string, unknown>): Record<string, string | number> =>
  Object.fromEntries(Object.entries(customValues).map(([key, value]) => [key, typeof value === 'number' ? value : String(value ?? '')]));

/**
 * Helper function to process labels from classification results
 */
export const processLabelsForCard = (classificationResults: ClassificationResults) =>
  classificationResults?.labels
    ? classificationResults.labels.map(label => ({
        name: label.labelName,
        severity: typeof label.severity === 'string' ? parseInt(label.severity, 10) || 0 : label.severity,
        label_num_map: label.labelNumMap,
        label_short_name: label.labelShortName,
      }))
    : [];

/**
 * Helper function to build classification metadata
 */
export const buildClassificationMetadata = (classificationResults: ClassificationResults) =>
  classificationResults
    ? {
        processing_time_ms: classificationResults.processingTimeMs ?? 0,
        confidence_score: classificationResults.confidenceScore ?? 0,
        execution_time_ms: classificationResults.executionTimeMs,
      }
    : undefined;

/**
 * Helper function to determine sampling status
 */
export const getSamplingStatus = (classificationResults: ClassificationResults, isSampled: boolean): 'sampled' | 'not_sampled' =>
  classificationResults || isSampled ? 'sampled' : 'not_sampled';

/**
 * Helper function to determine classification result
 */
export const getClassificationResult = (classificationResults: ClassificationResults): 'allowed' | 'rejected' =>
  classificationResults?.classificationType === 'harmful_content' ? 'rejected' : 'allowed';

/**
 * Convert ContentSearchItem to ContentCardItem
 */
// eslint-disable-next-line complexity
export const convertToContentCardItem = (message: ContentSearchItem): ContentCardItem => {
  // Extract data from the raw JSON objects
  const classificationResults = message.classificationResults as ClassificationResults;
  const contentMetadata = message.contentMetadata as ContentMetadata;

  // Extract nested data from contentMetadata
  const metadata = contentMetadata?.metadata || {};
  const subjectOwner = contentMetadata?.subjectOwner || {};
  const customValues = contentMetadata?.customValues || {};

  return {
    id: message.id,
    subjectId: contentMetadata?.subjectId ?? message.id,
    type: toContentType(message.contentType),
    text: getContentDisplayText(classificationResults?.transcript, contentMetadata?.contentUrl, contentMetadata?.audioFileUrl),
    topic: String(metadata?.world ?? 'Unknown'),
    topicName: message.topicName,
    language: classificationResults?.languageDetected,
    timestamp: message.createdAt,
    subjectOwner: {
      type: subjectOwner?.type ?? 'Unknown',
      userId: subjectOwner?.id ?? 'Unknown',
      username: (subjectOwner?.additionalInfo?.username as string) ?? 'Unknown User',
    },
    audioFileUrl: contentMetadata?.audioFileUrl,
    contentUrl: contentMetadata?.contentUrl,
    eventType: message.eventType,
    contentType: message.contentType,
    isSampled: message.isSampled,
    metadata: processMetadataForCard(metadata),
    customFields: processCustomValuesForCard(customValues),
    labels: processLabelsForCard(classificationResults),
    samplingStatus: getSamplingStatus(classificationResults, message.isSampled),
    classificationResult: getClassificationResult(classificationResults),
    classificationMetadata: buildClassificationMetadata(classificationResults),
  };
};

/**
 * Check if metadata is empty or has no meaningful values
 */
export const hasValidMetadata = (metadata: Record<string, unknown>): boolean => {
  if (!metadata || typeof metadata !== 'object') {
    return false;
  }

  const entries = Object.entries(metadata);

  return entries.length > 0 && entries.some(([, value]) => value !== null && value !== undefined && value !== '');
};
